/**
 * Site Header with navigation and theme toggle.
 */

import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Sun, Moon } from 'lucide-react'
import { useTheme } from '../state/ThemeContext'

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/pairs', label: 'bigflow.pairs' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
]

export function Header() {
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { toggleTheme, isDark } = useTheme()

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[var(--color-border)] bg-[var(--color-bg)]/80">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={isDark ? "/icon.png" : "/icon-dark.png"} 
              alt="bigflowdata" 
              className="h-8 w-8"
              onError={(e) => {
                // Fallback to white icon if dark doesn't exist
                (e.target as HTMLImageElement).src = "/icon.png"
              }}
            />
            <span className="text-lg font-bold">
              bigflow<span className="text-brand-500">data</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-brand-500'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Theme Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-[var(--color-border)] transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-[var(--color-text-secondary)]" />
              ) : (
                <Moon className="h-5 w-5 text-[var(--color-text-secondary)]" />
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-[var(--color-bg)] border-b border-[var(--color-border)]">
          <nav className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-brand-500/10 text-brand-500'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-border)] hover:text-[var(--color-text)]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  )
}
